import { useState, useEffect, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import { 
  getCategories, 
  createCategory, 
  updateCategory, 
  deleteCategory,
  Category 
} from "@/utils/adminApi";

// 生成唯一的纯数字slug
function generateNumericSlug(existingSlugs: string[]): string {
  // 生成6位时间戳数字
  const base = Date.now().toString().slice(-6);
  let slug = base;
  let counter = 1;
  
  // 如果已存在，添加递增后缀
  while (existingSlugs.includes(slug)) {
    slug = `${base}-${counter}`;
    counter++;
  }
  return slug;
}

// 100个 Remix Icon 图标
const iconOptions = [
  // 建筑与商业
  "ri-building-line", "ri-building-2-line", "ri-building-3-line", "ri-store-line", "ri-store-2-line",
  "ri-store-3-line", "ri-shopping-bag-line", "ri-shopping-bag-2-line", "ri-shopping-cart-line", "ri-shopping-cart-2-line",
  "ri-briefcase-line", "ri-briefcase-2-line", "ri-briefcase-3-line", "ri-briefcase-4-line", "ri-government-line",
  "ri-bank-line", "ri-hotel-line", "ri-hotel-2-line", "ri-fridge-line", "ri-archive-line",
  // 科技与开发
  "ri-code-line", "ri-code-s-line", "ri-code-box-line", "ri-code-s-slash-line", "ri-braces-line",
  "ri-git-branch-line", "ri-git-commit-line", "ri-git-merge-line", "ri-git-pull-request-line", "ri-git-repository-commits-line",
  "ri-terminal-line", "ri-terminal-window-line", "ri-parentheses-line", "ri-brackets-line", "ri Gustavo-line",
  "ri-robot-line", "ri-robot-2-line", "ri-ai-line", "ri-cloud-line", "ri-cloud-windy-line",
  "ri-cloudy-line", "ri-temp-line", "ri-fire-line", "ri-flashlight-line", "ri-battery-line",
  // 设计与创意
  "ri-palette-line", "ri-paint-brush-line", "ri-paint-brush-2-line", "ri-paint-line", "ri-pencil-line",
  "ri-pencil-ruler-line", "ri-pencil-ruler-2-line", "ri-scissors-line", "ri-ruler-line", "ri-ruler-2-line",
  "ri-crop-line", "ri-drag-move-line", "ri-layout-line", "ri-layout-2-line", "ri-layout-3-line",
  "ri-layout-4-line", "ri-layout-5-line", "ri-layout-6-line", "ri-collage-line", "ri-grid-line",
  "ri-hammer-line", "ri-tools-line", "ri-tools-2-line", "ri-wrench-line", "ri-screwdriver-line",
  // 营销与商业
  "ri-megaphone-line", "ri-megaphone-2-line", "ri-advertisement-line", "ri-bill-line", "ri-coupon-2-line",
  "ri-coupon-3-line", "ri-coupon-4-line", "ri-coupon-5-line", "ri-currency-line", "ri-money-cny-box-line",
  "ri-money-dollar-box-line", "ri-secure-payment-line", "ri-wallet-line", "ri-wallet-2-line", "ri-wallet-3-line",
  "ri-bar-chart-line", "ri-bar-chart-2-line", "ri-bar-chart-box-line", "ri-line-chart-line", "ri-pie-chart-line",
  "ri-pie-chart-2-line", "ri-pie-chart-3-line", "ri-donut-chart-line", "ri-gift-line", "ri-gift-2-line",
  // 医疗健康
  "ri-heart-line", "ri-heart-2-line", "ri-heart-3-line", "ri-heart-pulse-line", "ri-heart-add-line",
  "ri-medicine-bottle-line", "ri-thermometer-line", "ri-surgical-mask-line", "ri-stethoscope-line", "ri-psychology-line",
  "ri-hospital-line", "ri-ambulance-line", "ri-health-book-line", "ri-eye-line", "ri-eye-2-line",
  "ri-eye-3-line", "ri-brain-line", "ri-lungs-line", "ri-bone-line", "ri-capsule-line",
  // 教育与学习
  "ri-graduation-cap-line", "ri-school-line", "ri-book-line", "ri-book-2-line", "ri-book-3-line",
  "ri-book-open-line", "ri-booklet-line", "ri-newspaper-line", "ri-file-line", "ri-file-2-line",
  "ri-file-3-line", "ri-file-4-line", "ri-file-text-line", "ri-file-paper-line", "ri-file-paper-2-line",
  "ri-essay-line", "ri-pen-nib-line", "ri-quill-pen-line", "ri-mark-pen-line", "ri-flashlight-line",
  // 家居与生活
  "ri-home-line", "ri-home-2-line", "ri-home-3-line", "ri-home-4-line", "ri-home-5-line",
  "ri-home-6-line", "ri-home-8-line", "ri-sofa-line", "ri-armchair-line", "ri-hotel-bed-line",
  "ri-cup-line", "ri-coffee-line", "ri-cup-line", "ri-restaurant-line", "ri-restaurant-2-line",
  "ri-knife-line", "ri-spatula-line", "ri-basketball-line", "ri-football-line", "ri-billiards-line",
  // 汽车与交通
  "ri-car-line", "ri-caravan-line", "ri-taxi-line", "ri-taxi-wifi-line", "ri-bus-line",
  "ri-bus-2-line", "ri-bus-wifi-line", "ri-train-line", "ri-train-wifi-line", "ri-road-map-line",
  "ri-navigation-line", "ri-map-pin-line", "ri-map-pin-2-line", "ri-map-pin-3-line", "ri-landscape-line",
  "ri-globe-line", "ri-earth-line", "ri-plane-line", "ri-ship-line", "ri-sailboat-line",
  // 其他
  "ri-shield-line", "ri-shield-check-line", "ri-shield-star-line", "ri-shield-flash-line", "ri-secure-line",
  "ri-key-line", "ri-key-2-line", "ri-lock-line", "ri-lock-2-line", "ri-unlock-line",
];

// 8种图标颜色
const iconColors = [
  { name: "红色", value: "red", bg: "bg-red-50", text: "text-red-500" },
  { name: "白色", value: "white", bg: "bg-gray-100", text: "text-gray-600" },
  { name: "黑色", value: "black", bg: "bg-gray-800", text: "text-white" },
  { name: "蓝色", value: "blue", bg: "bg-blue-50", text: "text-blue-500" },
  { name: "绿色", value: "green", bg: "bg-green-50", text: "text-green-500" },
  { name: "紫色", value: "purple", bg: "bg-purple-50", text: "text-purple-500" },
  { name: "粉色", value: "pink", bg: "bg-pink-50", text: "text-pink-500" },
  { name: "橙色", value: "orange", bg: "bg-orange-50", text: "text-orange-500" },
];

// 颜色映射到 Tailwind 类
const colorBgMap: Record<string, string> = {
  red: "bg-red-50",
  white: "bg-gray-100",
  black: "bg-gray-800",
  blue: "bg-blue-50",
  green: "bg-green-50",
  purple: "bg-purple-50",
  pink: "bg-pink-50",
  orange: "bg-orange-50",
};

const colorTextMap: Record<string, string> = {
  red: "text-red-500",
  white: "text-gray-600",
  black: "text-white",
  blue: "text-blue-500",
  green: "text-green-500",
  purple: "text-purple-500",
  pink: "text-pink-500",
  orange: "text-orange-500",
};

interface CategoryWithChildren extends Category {
  subCategories: { id: string; name: string }[];
  is_nav?: string | number;
  icon_color?: string;
}

interface EditCatModalProps {
  cat: CategoryWithChildren | null;
  allCategories: Category[];
  onClose: () => void;
  onSave: (cat: CategoryWithChildren) => void;
}

function EditCatModal({ cat, allCategories, onClose, onSave }: EditCatModalProps) {
  const isNew = !cat?.id || cat.id.startsWith("new-");
  const [form, setForm] = useState<CategoryWithChildren>(
    cat ?? { 
      id: "new-" + Date.now(), 
      name: "", 
      slug: "", 
      icon: "ri-building-line",
      icon_color: "orange",
      status: "active", 
      is_nav: "0",
      sort_order: 0,
      parent_id: null,
      subCategories: [] 
    }
  );
  const [subInput, setSubInput] = useState("");
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [iconPage, setIconPage] = useState(1);
  const [slugManual, setSlugManual] = useState(!isNew);
  const iconsPerPage = 28;
  const totalPages = Math.ceil(iconOptions.length / iconsPerPage);
  const currentIcons = iconOptions.slice((iconPage - 1) * iconsPerPage, iconPage * iconsPerPage);

  const set = (k: keyof CategoryWithChildren, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  const handleNameChange = (name: string) => {
    set("name", name);
    if (!slugManual && name) {
      // 获取所有现有的 slug
      const existingSlugs = allCategories.map(c => c.slug).filter(Boolean);
      set("slug", generateNumericSlug(existingSlugs));
    }
  };

  const handleSlugChange = (slug: string) => {
    setSlugManual(true);
    // 只允许数字和短横线
    set("slug", slug.replace(/[^0-9-]/g, '').replace(/--+/g, '-').replace(/^-|-$/g, ''));
  };

  const addSub = () => {
    const name = subInput.trim();
    if (!name) return;
    set("subCategories", [...form.subCategories, { id: "sub-" + Date.now(), name }]);
    setSubInput("");
  };

  const removeSub = (id: string) => {
    set("subCategories", form.subCategories.filter((s) => s.id !== id));
  };

  const handleSave = () => {
    if (!form.name.trim()) return;
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">{isNew ? "新增分类" : "编辑分类"}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">分类名称 <span className="text-red-400">*</span></label>
              <input value={form.name} onChange={(e) => handleNameChange(e.target.value)} placeholder="如：企业服务" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">URL标识（slug）</label>
              <input value={form.slug} onChange={(e) => handleSlugChange(e.target.value)} placeholder="自动生成（数字）" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
              <p className="text-xs text-gray-400 mt-1">访问地址：/{form.slug || "..."}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1.5">分类图标与颜色</label>
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${colorBgMap[form.icon_color || 'orange']}`}>
                <i className={`${form.icon || 'ri-building-line'} ${colorTextMap[form.icon_color || 'orange']} text-2xl`} />
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => setShowIconPicker(!showIconPicker)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:border-orange-400 cursor-pointer text-left">
                  {showIconPicker ? "收起图标选择器" : "选择图标"}
                </button>
                {/* 颜色选择 */}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-400 mr-1">颜色:</span>
                  {iconColors.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => set("icon_color", c.value)}
                      title={c.name}
                      className={`w-6 h-6 rounded-full border-2 cursor-pointer transition-transform hover:scale-110 ${
                        form.icon_color === c.value ? "border-gray-800 scale-110" : "border-transparent"
                      } ${c.value === 'white' ? 'bg-gray-100' : c.bg}`}
                    >
                      {c.value === 'white' && <i className="ri-check-line text-xs text-gray-600" />}
                      {c.value !== 'white' && <span className={`${c.text} text-xs font-bold`}>{c.name[0]}</span>}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {/* 图标选择器 - 分页显示 */}
            {showIconPicker && (
              <div className="mt-3 p-4 border border-gray-200 rounded-xl bg-gray-50">
                {/* 分页控制 */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-500">第 {iconPage} / {totalPages} 页，共 {iconOptions.length} 个图标</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setIconPage(p => Math.max(1, p - 1))}
                      disabled={iconPage === 1}
                      className="w-7 h-7 flex items-center justify-center rounded-md bg-white border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line" />
                    </button>
                    {[1, 2, 3, 4].map(p => (
                      p <= totalPages && (
                        <button
                          key={p}
                          onClick={() => setIconPage(p)}
                          className={`w-7 h-7 flex items-center justify-center rounded-md text-xs cursor-pointer ${
                            iconPage === p ? "bg-orange-500 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          {p}
                        </button>
                      )
                    ))}
                    <button
                      onClick={() => setIconPage(p => Math.min(totalPages, p + 1))}
                      disabled={iconPage === totalPages}
                      className="w-7 h-7 flex items-center justify-center rounded-md bg-white border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line" />
                    </button>
                  </div>
                </div>
                {/* 图标网格 */}
                <div className="grid grid-cols-7 gap-1.5">
                  {currentIcons.map((icon) => (
                    <button
                      key={icon}
                      onClick={() => set("icon", icon)}
                      className={`w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-all ${
                        form.icon === icon 
                          ? `${colorBgMap[form.icon_color || 'orange']} ring-2 ring-orange-500 ring-offset-1` 
                          : "bg-white hover:bg-gray-100"
                      }`}
                    >
                      <i className={`${icon} text-lg ${form.icon === icon ? colorTextMap[form.icon_color || 'orange'] : 'text-gray-600'}`} />
                    </button>
                  ))}
                </div>
                {/* 关闭按钮 */}
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => setShowIconPicker(false)}
                    className="px-3 py-1.5 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer"
                  >
                    确认选择
                  </button>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1.5">父级分类</label>
            <select
              value={form.parent_id || ''}
              onChange={(e) => set("parent_id", e.target.value || null)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
            >
              <option value="">无（顶级分类）</option>
              {allCategories.filter(c => c.id !== form.id && !c.id.startsWith("new-")).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700">显示在导航栏</label>
              <p className="text-xs text-gray-500 mt-0.5">开启后该分类将出现在前台顶部导航</p>
            </div>
            <button
              onClick={() => set("is_nav", form.is_nav === '1' ? '0' : '1')}
              className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${form.is_nav === '1' ? "bg-orange-500" : "bg-gray-300"}`}
            >
              <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_nav === '1' ? "translate-x-4" : "translate-x-0"}`} />
            </button>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700">启用分类</label>
              <p className="text-xs text-gray-500 mt-0.5">开启后用户可查看该分类下的信息</p>
            </div>
            <button
              onClick={() => set("status", form.status === 'active' ? 'hidden' : 'active')}
              className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${form.status === 'active' ? "bg-green-500" : "bg-gray-300"}`}
            >
              <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.status === 'active' ? "translate-x-4" : "translate-x-0"}`} />
            </button>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-2">二级子分类</label>
            <div className="flex flex-wrap gap-2 mb-2 min-h-8">
              {form.subCategories.map((sub) => (
                <span key={sub.id} className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                  {sub.name}
                  <button onClick={() => removeSub(sub.id)} className="w-3.5 h-3.5 flex items-center justify-center hover:text-red-500 cursor-pointer">
                    <i className="ri-close-line text-xs" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={subInput}
                onChange={(e) => setSubInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addSub()}
                placeholder="输入子分类名称，回车添加"
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
              />
              <button onClick={addSub} className="px-3 py-2 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap">
                添加
              </button>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">取消</button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminCategoriesPage() {
  const [categories, setCategories] = useState<CategoryWithChildren[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [editTarget, setEditTarget] = useState<CategoryWithChildren | null>(null);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 加载分类数据
  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCategories();
      // 转换为带子分类的格式
      const flattened = flattenCategories(data);
      setCategories(flattened);
      setAllCategories(data);
    } catch (err: any) {
      setError(err.message || '加载分类失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // 将树形结构展平为一级列表，每个父分类显示其子分类
  const flattenCategories = (cats: Category[]): CategoryWithChildren[] => {
    return cats.map(cat => ({
      ...cat,
      subCategories: (cat.children || []).map(child => ({
        id: child.id,
        name: child.name
      }))
    }));
  };

  const handleSave = async (cat: CategoryWithChildren) => {
    try {
      const isNew = !cat.id || cat.id.startsWith("new-");
      
      // 构建API数据
      const apiData = {
        name: cat.name,
        slug: cat.slug || undefined,
        parent_id: cat.parent_id,
        icon: cat.icon,
        status: cat.status,
        is_nav: cat.is_nav === '1' || cat.is_nav === 1 ? '1' : '0',
      };
      
      if (isNew) {
        await createCategory(apiData);
        
        // 创建子分类
        for (const sub of cat.subCategories) {
          if (sub.id.startsWith("sub-")) {
            await createCategory({
              name: sub.name,
              parent_id: cat.id, // 注意：这里需要使用后端返回的实际ID
            });
          }
        }
      } else {
        await updateCategory(cat.id, apiData);
        
        // TODO: 处理子分类的更新（需要更复杂的逻辑来追踪新增/删除/修改的子分类）
      }
      
      setEditTarget(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      
      // 重新加载数据
      await loadCategories();
    } catch (err: any) {
      setError(err.message || '保存失败');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteCategory(id);
      await loadCategories();
    } catch (err: any) {
      setError(err.message || '删除失败');
    }
  };

  const handleToggle = async (id: string) => {
    try {
      const cat = categories.find(c => c.id === id);
      if (cat) {
        await updateCategory(id, {
          status: cat.status === 'active' ? 'hidden' : 'active'
        });
        await loadCategories();
      }
    } catch (err: any) {
      setError(err.message || '更新失败');
    }
  };

  const handleToggleNav = async (id: string) => {
    try {
      const cat = categories.find(c => c.id === id);
      if (cat) {
        const currentNav = cat.is_nav === '1' || cat.is_nav === 1 ? '1' : '0';
        await updateCategory(id, {
          is_nav: currentNav === '1' ? '0' : '1'
        });
        await loadCategories();
      }
    } catch (err: any) {
      setError(err.message || '更新失败');
    }
  };

  const moveUp = async (idx: number) => {
    if (idx === 0) return;
    try {
      // 生成按新位置排列的分类列表
      const sorted = [...categories];
      [sorted[idx], sorted[idx - 1]] = [sorted[idx - 1], sorted[idx]];
      // 每个位置分配唯一 sort_order，解决现有 sort_order 全为 0 的问题
      const updates = sorted.map((c, i) =>
        updateCategory(c.id, { sort_order: (i + 1) * 10 })
      );
      await Promise.all(updates);
      await loadCategories();
    } catch (err: any) {
      setError(err.message || '排序失败');
    }
  };

  const moveDown = async (idx: number) => {
    if (idx === categories.length - 1) return;
    try {
      const sorted = [...categories];
      [sorted[idx], sorted[idx + 1]] = [sorted[idx + 1], sorted[idx]];
      const updates = sorted.map((c, i) =>
        updateCategory(c.id, { sort_order: (i + 1) * 10 })
      );
      await Promise.all(updates);
      await loadCategories();
    } catch (err: any) {
      setError(err.message || '排序失败');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">加载中...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">分类设置</h1>
            <p className="text-sm text-gray-500 mt-1">管理前台信息分类，支持一级分类和二级子分类，可控制是否显示在导航栏</p>
          </div>
          <div className="flex items-center gap-3">
            {error && (
              <span className="text-sm text-red-500 flex items-center gap-1">
                <i className="ri-error-warning-line" />
                {error}
              </span>
            )}
            {saved && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <i className="ri-checkbox-circle-line" />
                已保存
              </span>
            )}
            <button
              onClick={() => setEditTarget({ 
                id: "new-" + Date.now(), 
                name: "", 
                slug: "", 
                icon: "ri-building-line",
                icon_color: "orange",
                status: "active", 
                is_nav: "0",
                sort_order: categories.length,
                parent_id: null,
                subCategories: [] 
              })}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              新增分类
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-5 text-xs text-gray-500 bg-white rounded-xl px-4 py-3 border border-gray-100">
          <span className="font-medium text-gray-600">图例说明：</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-orange-500 inline-block" />导航显示开关</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-500 inline-block" />分类启用开关</span>
        </div>

        {categories.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 py-16 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
              <i className="ri-folder-line text-gray-400 text-xl" />
            </div>
            <p className="text-gray-400 text-sm">暂无分类，点击右上角新增</p>
          </div>
        ) : (
          <div className="space-y-3">
            {categories.map((cat, idx) => (
              <div key={cat.id} className={`bg-white rounded-xl border transition-all ${cat.status === 'active' ? "border-gray-100" : "border-gray-100 opacity-60"}`}>
                <div className="flex items-center gap-4 px-5 py-4">
                  {/* Sort Buttons */}
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveUp(idx)} disabled={idx === 0} className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-600 disabled:opacity-30 cursor-pointer">
                      <i className="ri-arrow-up-s-line text-sm" />
                    </button>
                    <button onClick={() => moveDown(idx)} disabled={idx === categories.length - 1} className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-600 disabled:opacity-30 cursor-pointer">
                      <i className="ri-arrow-down-s-line text-sm" />
                    </button>
                  </div>

                  {/* Rank */}
                  <span className="text-xs text-gray-300 font-mono w-4 text-center">{idx + 1}</span>

                  {/* Icon */}
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 ${colorBgMap[cat.icon_color as string] || 'bg-orange-50'}`}>
                    <i className={`${cat.icon || 'ri-building-line'} ${colorTextMap[cat.icon_color as string] || 'text-orange-500'} text-lg`} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-800">{cat.name}</span>
                      {cat.slug && <span className="text-xs text-gray-400 font-mono">/{cat.slug}</span>}
                      {cat.status === 'hidden' && <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">已禁用</span>}
                      {(cat.is_nav === '1' || cat.is_nav === 1) && (
                        <span className="text-xs bg-orange-50 text-orange-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <i className="ri-navigation-line text-xs" />导航显示
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {cat.subCategories.map((sub) => (
                        <span key={sub.id} className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{sub.name}</span>
                      ))}
                      {cat.subCategories.length === 0 && <span className="text-xs text-gray-300">暂无子分类</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-400">导航</span>
                      <button
                        onClick={() => handleToggleNav(cat.id)}
                        title={(cat.is_nav === '1' || cat.is_nav === 1) ? "点击隐藏导航" : "点击显示导航"}
                        className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer ${(cat.is_nav === '1' || cat.is_nav === 1) ? "bg-orange-500" : "bg-gray-200"}`}
                      >
                        <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${(cat.is_nav === '1' || cat.is_nav === 1) ? "translate-x-4" : "translate-x-0"}`} />
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-400">启用</span>
                      <button
                        onClick={() => handleToggle(cat.id)}
                        title={cat.status === 'active' ? "点击禁用" : "点击启用"}
                        className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer ${cat.status === 'active' ? "bg-green-500" : "bg-gray-200"}`}
                      >
                        <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${cat.status === 'active' ? "translate-x-4" : "translate-x-0"}`} />
                      </button>
                    </div>
                    <button onClick={() => window.open(`/category/${cat.slug}`, '_blank')} className="px-3 py-1.5 text-xs border border-gray-200 text-blue-600 rounded-lg hover:border-blue-400 hover:text-blue-500 cursor-pointer whitespace-nowrap flex items-center gap-1">
                      <i className="ri-external-link-line text-xs" />
                      访问
                    </button>
                    <button onClick={() => setEditTarget(cat)} className="px-3 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-orange-400 hover:text-orange-500 cursor-pointer whitespace-nowrap">
                      编辑
                    </button>
                    <button onClick={() => handleDelete(cat.id)} className="px-3 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-red-300 hover:text-red-500 cursor-pointer whitespace-nowrap">
                      删除
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editTarget && (
        <EditCatModal
          cat={editTarget}
          allCategories={allCategories}
          onClose={() => setEditTarget(null)}
          onSave={handleSave}
        />
      )}
    </AdminLayout>
  );
}
